import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, Check, AlertCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchPublicInvite, acceptInvite } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/contexts/AuthContext";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(token ? null : "Missing invite token.");
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetchPublicInvite(token)
      .then((inv) => {
        if (!cancelled) setInvite(inv);
      })
      .catch((e) => {
        if (!cancelled)
          setError(
            formatApiErrorDetail(e.response?.data?.detail) ||
              "This invite is invalid or has already been used."
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleAccept() {
    setAccepting(true);
    setError(null);
    try {
      const res = await acceptInvite(token);
      setAccepted(true);
      setTimeout(() => navigate(`/project/${res.project_id}`), 1200);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAccepting(false);
    }
  }

  function goToLogin() {
    const next = `/accept-invite?token=${encodeURIComponent(token)}`;
    navigate(`/login?next=${encodeURIComponent(next)}`);
  }

  function goToRegister() {
    const next = `/accept-invite?token=${encodeURIComponent(token)}`;
    navigate(
      `/register?next=${encodeURIComponent(next)}&email=${encodeURIComponent(
        invite?.email || ""
      )}`
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <div
        className="bg-white border border-stone-200 rounded-2xl shadow-sm p-8 max-w-md w-full"
        data-testid="accept-invite-card"
      >
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-10">
            <Loader2 className="w-6 h-6 animate-spin text-stone-500" />
            <p className="text-sm text-stone-500">Loading invite…</p>
          </div>
        ) : error && !invite ? (
          <div className="text-center space-y-4">
            <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-2xl text-stone-900">
              Invite unavailable
            </h1>
            <p
              className="text-sm text-stone-500"
              data-testid="accept-invite-error"
            >
              {error}
            </p>
            <Button asChild className="rounded-full">
              <Link to="/">Go home</Link>
            </Button>
          </div>
        ) : accepted ? (
          <div className="text-center space-y-4" data-testid="accept-invite-success">
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 flex items-center justify-center">
              <Check className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
            </div>
            <h1 className="font-display text-2xl text-stone-900">
              You&apos;re in!
            </h1>
            <p className="text-sm text-stone-500">
              Opening the project…
            </p>
          </div>
        ) : invite ? (
          <div className="space-y-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">
                  Atelier Studio invitation
                </p>
                <h1 className="font-display text-2xl text-stone-900 leading-tight mt-1">
                  Join {invite.project_name}
                </h1>
              </div>
            </div>

            <div className="text-sm text-stone-600 leading-relaxed">
              {invite.invited_by_name ? (
                <>
                  <strong className="text-stone-900">
                    {invite.invited_by_name}
                  </strong>{" "}
                  invited{" "}
                </>
              ) : (
                <>You&apos;ve been invited </>
              )}
              <strong className="text-stone-900">{invite.email}</strong> to
              collaborate as <strong>{invite.role}</strong>.
            </div>

            {error && (
              <p
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3"
                data-testid="accept-invite-error"
              >
                {error}
              </p>
            )}

            {!user ? (
              <div className="space-y-3">
                <p className="text-xs text-stone-500">
                  Sign in with{" "}
                  <strong className="text-stone-900">{invite.email}</strong> to
                  accept this invitation.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={goToLogin}
                    data-testid="accept-invite-login"
                    className="rounded-full flex-1 bg-stone-900 text-white hover:bg-stone-800"
                  >
                    Sign in
                  </Button>
                  <Button
                    variant="outline"
                    onClick={goToRegister}
                    data-testid="accept-invite-register"
                    className="rounded-full flex-1"
                  >
                    Create account
                  </Button>
                </div>
              </div>
            ) : user.email?.toLowerCase() !== invite.email.toLowerCase() ? (
              <div className="space-y-3">
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                  You&apos;re signed in as{" "}
                  <strong>{user.email}</strong>, but this invite is for{" "}
                  <strong>{invite.email}</strong>. Sign out and sign in with the
                  correct account.
                </p>
                <Button
                  variant="outline"
                  onClick={goToLogin}
                  data-testid="accept-invite-switch-account"
                  className="rounded-full w-full"
                >
                  Switch account
                </Button>
              </div>
            ) : (
              <Button
                onClick={handleAccept}
                disabled={accepting}
                data-testid="accept-invite-accept"
                className="rounded-full w-full bg-stone-900 text-white hover:bg-stone-800"
              >
                {accepting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
                )}
                Accept invitation
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
